import React from 'react';
import {
  HCardButton,
  HearthIcon,
  SignalBars,
  HChip,
} from '../h';

/* v9 4.1 Hearth card.

   Layout:
     row 1: [Owner|Invited] role badge + Hearth name + (wifi bars) + chevron
     row 2: coloured dot + short status phrase ("At home", "Asleep", …)
     row 3: pill chips — optional [Primary] + optional [Auto Mode]

   Rationale (per the user's redesign note):
   - The role (Owner vs Invited/Co-Manager) goes inline with the name so
     you see at a glance what you can *do* on this Hearth.
   - Only the signed-in account's Primary Hearth gets a chip; other
     Hearths render no place-relationship chip at all. The chip row
     keeps a reserved min-height so cards stay the same size whether or
     not any chips are shown.
   - Auto Mode is still a peer chip alongside the Primary chip.
   - Icon is always solid (v3 mockup language).
*/
export function HearthCard({ family, isPrimary, isOwner, onClick }) {
  const f = family || {};
  const status = resolveStatus(f);

  return (
    <HCardButton onClick={onClick} className="p-4">
      <div className="flex items-start gap-3">
        <HearthIcon size="sm" className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {/* Row 1: name + optional "Invited" badge + wifi. Owners get no
              badge — it's the common case and adding one everywhere is
              noise. Only co-managers see an "Invited" pill, sitting
              AFTER the name so the Hearth name leads. */}
          <div className="flex items-center gap-2">
            <div className="text-[15px] font-semibold text-slate-900 truncate">
              {f.familyName || '…'}
            </div>
            {!isOwner && <RoleBadge />}
            {f.isOnline && f.wifiRssiDbm != null && (
              <span className="ml-auto flex-shrink-0">
                <SignalBars rssiDbm={f.wifiRssiDbm} />
              </span>
            )}
          </div>
          {/* Row 2: status line — dot + simple phrase */}
          <div className="mt-1">
            <StatusLine status={status} />
          </div>
          {/* Row 3: primary + auto-mode chips. min-h keeps the row
              reserved even when both chips are absent so every card
              has the same height. */}
          <div className="mt-2 flex items-center gap-1.5 flex-wrap min-h-[24px]">
            {isPrimary && (
              <HChip className="text-[11px] bg-slate-900 text-white border-slate-900">
                Primary
              </HChip>
            )}
            {f.autoMode && (
              <HChip className="text-[11px] bg-indigo-50 text-indigo-700 border-indigo-200">
                Auto Mode
              </HChip>
            )}
          </div>
        </div>
        <Chevron />
      </div>
    </HCardButton>
  );
}

/* Grey "Invited" pill rendered in front of a Hearth name when the
   signed-in account is a co-manager rather than the owner. Owners
   intentionally get no badge — it's the default case. */
function RoleBadge() {
  return (
    <HChip className="bg-slate-100 text-slate-700 border-slate-200 text-[10px] uppercase tracking-wide font-semibold flex-shrink-0">
      Invited
    </HChip>
  );
}

function Chevron() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#94a3b8"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0 mt-0.5"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function StatusLine({ status }) {
  return (
    <div className="flex items-center gap-1.5 text-[12px] text-slate-600">
      <span
        className={`inline-block w-[6px] h-[6px] rounded-full flex-shrink-0 ${
          status.active ? 'bg-emerald-500' : 'bg-slate-300'
        }`}
        aria-hidden="true"
      />
      <span className="truncate">{status.text}</span>
    </div>
  );
}

/* Resolve the visible state from useFamiliesData's fields. Matches the
   v3 mockup's binary active/inactive dot colouring, with text supplying
   the detail. */
function resolveStatus(f) {
  if (f.hasEverSignedIn === false) {
    return { active: false, text: 'Not yet connected' };
  }
  if (!f.isOnline) {
    const ago = timeAgo(f.onlinePingMs);
    return { active: false, text: `Offline${ago ? ` · last seen ${ago}` : ''}` };
  }
  if (f.screen === 'sleep') {
    return { active: false, text: 'Asleep' };
  }
  const presence = resolvePresence(f.screen, f.iglooLinks);
  if (presence) {
    return { active: true, text: `In ${presence.name}` };
  }
  return { active: true, text: 'At home' };
}

/* `screen` is what HigView.kt writes to deviceManagement/{fid}/log/system.screen:
     'home' | 'loading' | 'training' | 'sleep'   → not in any Higlo
     '<iglooID>'                                  → in that Higlo (no position marker)
     '<iglooID>-Left' / '<iglooID>-Centre' / '<iglooID>-Right'
                                                  → in that Higlo at a specific position
   iglooIDs themselves may contain hyphens (UUID-style), so strip the trailing
   position marker rather than splitting on the first '-'. */
const POSITION_SUFFIX_RE = /-(Left|Centre|Right)$/;

function resolvePresence(screen, iglooLinks = []) {
  if (!screen) return null;
  if (['home', 'loading', 'training', 'sleep'].includes(screen)) return null;
  const iglooID = screen.replace(POSITION_SUFFIX_RE, '');
  const link = iglooLinks.find((l) => l.iglooID === iglooID);
  return { iglooID, name: link?.name || 'a Higlo' };
}

function timeAgo(ms) {
  if (!ms) return null;
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
